import { download, downloadPost, request } from './client';

/** A workbook laid out on the client: each sheet a name and a grid of string/number cells. */
export interface ExcelExport {
  fileName: string;
  sheets: { name: string; rows: (string | number)[][] }[];
}
import type { AuditStatus } from './audits';

/**
 * Reports people read rather than fill in.
 *
 * One of them, deliberately: the system this replaces had eight and the customer opened
 * one. The whole of the investment goes into that one filtering properly.
 */

export type VisitTrend = 'Unknown' | 'Steady' | 'Improved' | 'Declined';

/** Whether the people named for this visit type were told. Read from the mail queue. */
export type MailDelivery = 'None' | 'Queued' | 'Sent' | 'Failed';

export interface VisitResultRow {
  id: string;
  reference: string | null;

  /** Whoever holds the seat for this store's region today — who to call about it. */
  regionalManager: string | null;

  locationCode: string;
  locationName: string;
  locationType: string | null;
  region: string | null;

  visitType: string | null;
  formName: string;
  auditorName: string;
  submittedAt: string | null;
  status: AuditStatus;

  score: number | null;
  band: string | null;
  bandColour: string | null;
  zeroedByCritical: boolean;

  /** The same store's previous visit of the same type. Null when this is the first. */
  previousScore: number | null;
  previousBand: string | null;
  previousSubmittedAt: string | null;

  trend: VisitTrend;

  /** Whether the PDF has already been drawn. */
  hasReport: boolean;
  mail: MailDelivery;
}

export interface VisitResultsSummary {
  count: number;
  averageScore: number | null;
  zeroedByCritical: number;
  belowThreshold: number;
}

export interface FilterOption {
  id: string;
  text: string;
}

/**
 * What the filters may be set to, for this reader.
 *
 * Sent with the report rather than fetched from the definition screens: the person reading
 * this holds audits.read and usually nothing else, so those would answer 403 and leave
 * every dropdown silently empty. These are also narrowed to the stores they can reach.
 */
export interface VisitFilterOptions {
  operations: FilterOption[];
  regions: FilterOption[];
  locations: FilterOption[];
  locationTypes: FilterOption[];
  auditors: FilterOption[];
  visitTypes: FilterOption[];
  /** Region id → operation id, so choosing an operation narrows the regions. */
  regionOperations: Record<string, string>;
  /** Location id → region id, for the same reason. */
  locationRegions: Record<string, string>;
}

export interface VisitResults {
  summary: VisitResultsSummary;
  filters: VisitFilterOptions;
  rows: VisitResultRow[];
  /** More matched than were returned. Said out loud rather than quietly truncated. */
  truncated: boolean;
}

export interface VisitFilter {
  from?: string;
  to?: string;
  operationId?: string;
  regionId?: string;
  locationId?: string;
  locationTypeId?: string;
  auditorUserId?: string;
  visitTypeId?: string;
  belowScore?: number;
}

function query(filter: VisitFilter): string {
  const parts = new URLSearchParams();

  for (const [key, value] of Object.entries(filter)) {
    // An empty select is "all of them", so it is left out of the query entirely rather than
    // sent as an empty string the server would have to decide about.
    if (value !== undefined && value !== null && value !== '') parts.set(key, String(value));
  }

  const q = parts.toString();
  return q ? `?${q}` : '';
}

// ------------------------------------------------------------------- follow-up

/**
 * The report of what happened after the visits: not the score, but whether the actions the
 * visits raised were closed, in time, and how far the open ones have drifted.
 */
export interface FollowUpSummary {
  total: number;
  open: number;
  overdue: number;
  completed: number;
  /** Closed without being resolved — the work that could not be finished. */
  unresolved: number;
  /** Share of finished actions that finished on time, or null if none have. */
  onTimeClosureRate: number | null;
  /** Average whole days from raised to finished, or null if none have. */
  averageDaysToClose: number | null;
  /** Overdue a week or less. */
  agingFresh: number;
  /** Overdue eight to thirty days. */
  agingAging: number;
  /** Overdue more than a month. */
  agingStale: number;
}

export interface FollowUpActionRow {
  id: string;
  locationCode: string;
  locationName: string;
  region: string | null;
  operation: string | null;
  locationType: string | null;
  regionalManager: string | null;
  /** The category the question belongs to. */
  category: string;
  /** The question the action was raised against — the finding. */
  finding: string;
  assignee: string;
  /** Status as its enum name; the screen gives it words. */
  status: string;
  raisedOn: string;
  dueOn: string;
  onTime: boolean;
  overdueDays: number | null;
  daysToClose: number | null;
  daysOpen: number | null;
}

export interface FollowUpFilterOptions {
  operations: FilterOption[];
  regions: FilterOption[];
  locations: FilterOption[];
  locationTypes: FilterOption[];
  regionOperations: Record<string, string>;
  locationRegions: Record<string, string>;
}

export interface FollowUpResults {
  summary: FollowUpSummary;
  filters: FollowUpFilterOptions;
  /** The actions in scope, worst follow-up first. The grid groups and totals them. */
  actions: FollowUpActionRow[];
}

export interface FollowUpFilter {
  from?: string;
  to?: string;
  operationId?: string;
  regionId?: string;
  locationId?: string;
  locationTypeId?: string;
}

export const reports = {
  visits: (filter: VisitFilter = {}) =>
    request<VisitResults>(`/api/v1/reports/visits${query(filter)}`),

  /**
   * The follow-up dashboard. Which stores have stopped closing what they were told to.
   *
   * <p>The date range scopes actions by when they were raised; the open and overdue counts
   * are always as of today, because a follow-up report is about the state of things now.</p>
   */
  followUp: (filter: FollowUpFilter = {}) =>
    request<FollowUpResults>(`/api/v1/reports/follow-up${query(filter)}`),

  /** Turns a client-laid-out workbook into an .xlsx; the grid decides its shape, the server writes it. */
  exportXlsx: (payload: ExcelExport) => downloadPost('/api/v1/reports/export/xlsx', payload),

  /**
   * The same rows as a spreadsheet, from the same filter.
   *
   * <p>One query shared with the screen above it: an export that can disagree with what
   * somebody was looking at when they clicked is worse than no export.</p>
   */
  excel: (filter: VisitFilter = {}) =>
    download(`/api/v1/reports/visits/excel${query(filter)}`),

  /**
   * Every store this reader can reach, and how each province is doing.
   *
   * <p>Takes no filter. The map is what somebody opens to find out where to look, and a
   * map that opened already filtered would answer a question nobody had asked.</p>
   */
  map: () => request<MapResults>('/api/v1/reports/map'),
};

// ------------------------------------------------------------------- the map

/** Which figure a store's hover card shows. The customer chooses these. */
export type MapKpi = 'LastVisit' | 'OpenActions' | 'Trend' | 'YearToDate';

export interface MapProvince {
  /** Matched to the drawn shape by name — the shapes know nothing about this database. */
  name: string;
  locations: number;
  averageScore: number | null;
  band: string | null;
  bandColour: string | null;
  openActions: number;
  overdueActions: number;
}

export interface MapLocation {
  id: string;
  code: string;
  name: string;

  city: string | null;
  district: string | null;
  region: string | null;
  locationType: string | null;

  /** Where it actually is, for the few stores somebody has entered coordinates for. */
  latitude: number | null;
  longitude: number | null;

  lastVisitAt: string | null;
  score: number | null;
  band: string | null;
  bandColour: string | null;
  visitType: string | null;
  zeroedByCritical: boolean;

  openActions: number;
  overdueActions: number;

  previousScore: number | null;
  trend: VisitTrend;

  visitsThisYear: number;
  averageThisYear: number | null;
}

export interface MapSummary {
  locations: number;
  visited: number;
  averageScore: number | null;
  openActions: number;
  overdueActions: number;
}

export interface MapResults {
  provinces: MapProvince[];
  locations: MapLocation[];
  /** In the order the card shows them, as the customer has set them. */
  kpis: MapKpi[];
  summary: MapSummary;
}
